import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ConsentSummary {
  consent_type: string;
  granted: number;
  revoked: number;
}

interface AgeBlock {
  id: string;
  user_id: string;
  result: string;
  context: string;
  created_at: string;
}

interface DeletionRequest {
  id: string;
  user_id: string;
  requested_at: string;
  scheduled_for: string;
  status: string;
  daysLeft: number;
}

interface VendorLog {
  id: string;
  vendor_category: string;
  check_type: string;
  status: string;
  detail: string | null;
  checked_at: string;
  notified_admin: boolean;
}

const TABS = ['Compliance', 'Exclusões', 'Cancelamentos', 'Fornecedores'] as const;
type Tab = (typeof TABS)[number];

const STATUS_COLORS: Record<string, string> = {
  ok: '#4ade80',
  warning: '#f59e0b',
  violation: '#ef4444',
  pending: '#f59e0b',
  in_progress: '#60a5fa',
  completed: '#4ade80',
  cancelled: '#6b7280',
};

// ─── Sub-panels ──────────────────────────────────────────────────────────────

function CompliancePanel() {
  const [consents, setConsents] = useState<ConsentSummary[]>([]);
  const [ageBlocks, setAgeBlocks] = useState<AgeBlock[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const since30 = new Date(Date.now() - 30 * 86400000).toISOString();

      const [{ data: consentData }, { data: ageData }] = await Promise.all([
        supabase
          .from('consent_log')
          .select('consent_type, action')
          .gte('created_at', since30),
        supabase
          .from('age_validation_log')
          .select('id, user_id, result, context, created_at')
          .neq('result', 'passed')
          .gte('created_at', since30)
          .order('created_at', { ascending: false })
          .limit(50),
      ]);

      // Aggregate consent by type
      const map: Record<string, { granted: number; revoked: number }> = {};
      for (const row of consentData ?? []) {
        if (!map[row.consent_type]) map[row.consent_type] = { granted: 0, revoked: 0 };
        if (row.action === 'granted') map[row.consent_type].granted++;
        else map[row.consent_type].revoked++;
      }
      setConsents(
        Object.entries(map).map(([consent_type, v]) => ({ consent_type, ...v }))
      );
      setAgeBlocks(ageData ?? []);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <p style={{ color: '#8A7F6E' }}>Carregando...</p>;

  return (
    <div>
      <h3 style={{ color: '#D4A017', marginBottom: 12 }}>Consentimentos, últimos 30 dias</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 32, fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#1a1a2a' }}>
            <th style={TH}>Tipo</th>
            <th style={TH}>Concedidos</th>
            <th style={TH}>Revogados</th>
          </tr>
        </thead>
        <tbody>
          {consents.length === 0 && (
            <tr><td colSpan={3} style={{ ...TD, color: '#8A7F6E' }}>Nenhum registro.</td></tr>
          )}
          {consents.map(c => (
            <tr key={c.consent_type} style={{ borderBottom: '1px solid #2a2a3a' }}>
              <td style={TD}>{c.consent_type}</td>
              <td style={{ ...TD, color: '#4ade80' }}>{c.granted}</td>
              <td style={{ ...TD, color: '#ef4444' }}>{c.revoked}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ color: '#D4A017', marginBottom: 12 }}>Bloqueios por menoridade, últimos 30 dias</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#1a1a2a' }}>
            <th style={TH}>Data</th>
            <th style={TH}>Usuário</th>
            <th style={TH}>Resultado</th>
            <th style={TH}>Contexto</th>
          </tr>
        </thead>
        <tbody>
          {ageBlocks.length === 0 && (
            <tr><td colSpan={4} style={{ ...TD, color: '#8A7F6E' }}>Nenhum bloqueio.</td></tr>
          )}
          {ageBlocks.map(b => (
            <tr key={b.id} style={{ borderBottom: '1px solid #2a2a3a' }}>
              <td style={TD}>{new Date(b.created_at).toLocaleDateString('pt-BR')}</td>
              <td style={{ ...TD, fontFamily: 'monospace', fontSize: 11 }}>{b.user_id.slice(0, 8)}…</td>
              <td style={{ ...TD, color: '#ef4444' }}>{b.result}</td>
              <td style={TD}>{b.context}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeletionPanel() {
  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('data_deletion_requests')
      .select('id, user_id, requested_at, scheduled_for, status')
      .in('status', ['pending', 'in_progress'])
      .order('scheduled_for', { ascending: true });

    const now = Date.now();
    setRequests(
      (data ?? []).map(r => ({
        ...r,
        daysLeft: Math.ceil((new Date(r.scheduled_for).getTime() - now) / 86400000),
      }))
    );
    setLoading(false);
  }

  async function markInProgress(id: string) {
    setProcessing(id);
    await supabase.from('data_deletion_requests').update({ status: 'in_progress' }).eq('id', id);
    await load();
    setProcessing(null);
  }

  if (loading) return <p style={{ color: '#8A7F6E' }}>Carregando...</p>;

  return (
    <div>
      <h3 style={{ color: '#D4A017', marginBottom: 12 }}>Solicitações de Exclusão Pendentes</h3>
      {requests.length === 0 && (
        <p style={{ color: '#4ade80' }}>✅ Nenhuma exclusão pendente.</p>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#1a1a2a' }}>
            <th style={TH}>Usuário</th>
            <th style={TH}>Solicitado em</th>
            <th style={TH}>Prazo legal</th>
            <th style={TH}>Dias restantes</th>
            <th style={TH}>Status</th>
            <th style={TH}>Ação</th>
          </tr>
        </thead>
        <tbody>
          {requests.map(r => (
            <tr
              key={r.id}
              style={{
                borderBottom: '1px solid #2a2a3a',
                background: r.daysLeft <= 5 ? '#3a1a1a' : undefined,
              }}
            >
              <td style={{ ...TD, fontFamily: 'monospace', fontSize: 11 }}>{r.user_id.slice(0, 8)}…</td>
              <td style={TD}>{new Date(r.requested_at).toLocaleDateString('pt-BR')}</td>
              <td style={TD}>{new Date(r.scheduled_for).toLocaleDateString('pt-BR')}</td>
              <td style={{ ...TD, color: r.daysLeft <= 5 ? '#ef4444' : '#F5F0E8', fontWeight: r.daysLeft <= 5 ? 700 : 400 }}>
                {r.daysLeft <= 0 ? 'VENCIDO' : `${r.daysLeft}d`}
              </td>
              <td style={{ ...TD, color: STATUS_COLORS[r.status] ?? '#F5F0E8' }}>{r.status}</td>
              <td style={TD}>
                {r.status === 'pending' && (
                  <button
                    onClick={() => markInProgress(r.id)}
                    disabled={processing === r.id}
                    style={BTN_SM}
                  >
                    {processing === r.id ? '…' : 'Processar'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CancellationsPanel() {
  const [rows, setRows] = useState<{ id: string; user_id: string; created_at: string; consent_type: string; action: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const since30 = new Date(Date.now() - 30 * 86400000).toISOString();
      const { data } = await supabase
        .from('consent_log')
        .select('id, user_id, consent_type, action, created_at')
        .in('consent_type', ['cancellation_policy', 'refund_7days'])
        .gte('created_at', since30)
        .order('created_at', { ascending: false })
        .limit(100);
      setRows(data ?? []);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <p style={{ color: '#8A7F6E' }}>Carregando...</p>;

  const cancellations = rows.filter(r => r.consent_type === 'cancellation_policy');
  const refunds7 = rows.filter(r => r.consent_type === 'refund_7days');

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <StatCard label="Cancelamentos (30d)" value={cancellations.length} color="#f59e0b" />
        <StatCard label="Reembolsos 7d (30d)" value={refunds7.length} color="#60a5fa" />
      </div>

      <h3 style={{ color: '#D4A017', marginBottom: 12 }}>Eventos recentes, últimos 30 dias</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#1a1a2a' }}>
            <th style={TH}>Data</th>
            <th style={TH}>Usuário</th>
            <th style={TH}>Tipo</th>
            <th style={TH}>Ação</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={4} style={{ ...TD, color: '#8A7F6E' }}>Nenhum registro.</td></tr>
          )}
          {rows.map(r => (
            <tr key={r.id} style={{ borderBottom: '1px solid #2a2a3a' }}>
              <td style={TD}>{new Date(r.created_at).toLocaleDateString('pt-BR')}</td>
              <td style={{ ...TD, fontFamily: 'monospace', fontSize: 11 }}>{r.user_id.slice(0, 8)}…</td>
              <td style={TD}>{r.consent_type}</td>
              <td style={{ ...TD, color: r.action === 'granted' ? '#4ade80' : '#ef4444' }}>{r.action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VendorPanel() {
  const [logs, setLogs] = useState<VendorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'ok' | 'warning' | 'violation'>('all');
  const [resolving, setResolving] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const since30 = new Date(Date.now() - 30 * 86400000).toISOString();
    const { data } = await supabase
      .from('vendor_compliance_log')
      .select('id, vendor_category, check_type, status, detail, checked_at, notified_admin')
      .gte('checked_at', since30)
      .order('checked_at', { ascending: false });
    setLogs(data ?? []);
    setLoading(false);
  }

  async function resolve(id: string) {
    setResolving(id);
    await supabase.from('vendor_compliance_log').update({ notified_admin: true }).eq('id', id);
    setLogs(prev => prev.map(l => l.id === id ? { ...l, notified_admin: true } : l));
    setResolving(null);
  }

  const filtered = filter === 'all' ? logs : logs.filter(l => l.status === filter);

  if (loading) return <p style={{ color: '#8A7F6E' }}>Carregando...</p>;

  const violations = logs.filter(l => l.status === 'violation').length;
  const warnings   = logs.filter(l => l.status === 'warning').length;
  const ok         = logs.filter(l => l.status === 'ok').length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <StatCard label="OK" value={ok} color="#4ade80" />
        <StatCard label="Alertas" value={warnings} color="#f59e0b" />
        <StatCard label="Violações" value={violations} color="#ef4444" />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['all', 'ok', 'warning', 'violation'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              ...BTN_SM,
              background: filter === f ? '#D4A017' : '#1a1a2a',
              color: filter === f ? '#0A0D1A' : '#F5F0E8',
            }}
          >
            {f === 'all' ? 'Todos' : f}
          </button>
        ))}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#1a1a2a' }}>
            <th style={TH}>Data</th>
            <th style={TH}>Fornecedor</th>
            <th style={TH}>Verificação</th>
            <th style={TH}>Status</th>
            <th style={TH}>Detalhe</th>
            <th style={TH}>Ação</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr><td colSpan={6} style={{ ...TD, color: '#8A7F6E' }}>Nenhum registro.</td></tr>
          )}
          {filtered.map(l => (
            <tr
              key={l.id}
              style={{
                borderBottom: '1px solid #2a2a3a',
                background: l.status === 'violation' ? '#2a1010' : l.status === 'warning' ? '#2a2210' : undefined,
              }}
            >
              <td style={TD}>{new Date(l.checked_at).toLocaleDateString('pt-BR')}</td>
              <td style={TD}>{l.vendor_category}</td>
              <td style={{ ...TD, fontFamily: 'monospace', fontSize: 11 }}>{l.check_type}</td>
              <td style={{ ...TD, color: STATUS_COLORS[l.status] ?? '#F5F0E8', fontWeight: 700 }}>{l.status}</td>
              <td style={{ ...TD, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {l.detail ?? '-'}
              </td>
              <td style={TD}>
                {!l.notified_admin && (
                  <button
                    onClick={() => resolve(l.id)}
                    disabled={resolving === l.id}
                    style={BTN_SM}
                  >
                    {resolving === l.id ? '…' : 'Resolver'}
                  </button>
                )}
                {l.notified_admin && <span style={{ color: '#4ade80', fontSize: 11 }}>✓</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ background: '#1a1a2a', borderRadius: 8, padding: '12px 20px', textAlign: 'center', minWidth: 100 }}>
      <div style={{ fontSize: 28, color, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#8A7F6E', marginTop: 4 }}>{label}</div>
    </div>
  );
}

const TH: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', color: '#8A7F6E', fontWeight: 600, fontSize: 12 };
const TD: React.CSSProperties = { padding: '8px 10px', color: '#F5F0E8' };
const BTN_SM: React.CSSProperties = {
  background: '#2a2a3a', color: '#F5F0E8', border: '1px solid #3a3a4a',
  borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
};

// ─── Main page ────────────────────────────────────────────────────────────────

export function AdminCompliancePage() {
  const [tab, setTab] = useState<Tab>('Compliance');

  return (
    <div style={{ padding: '24px 32px', fontFamily: 'sans-serif', color: '#F5F0E8', maxWidth: 1100 }}>
      <AdminPageHeader
        title="Compliance & LGPD"
        objective="Monitoramento de conformidade, exclusões de dados, cancelamentos e fornecedores."
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #2a2a3a' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '10px 18px', fontSize: 14, fontWeight: 600,
              color: tab === t ? '#D4A017' : '#8A7F6E',
              borderBottom: tab === t ? '2px solid #D4A017' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Panel */}
      <div style={{ background: '#0F1220', borderRadius: 12, padding: 24, border: '1px solid #2a2a3a' }}>
        {tab === 'Compliance'    && <CompliancePanel />}
        {tab === 'Exclusões'     && <DeletionPanel />}
        {tab === 'Cancelamentos' && <CancellationsPanel />}
        {tab === 'Fornecedores'  && <VendorPanel />}
      </div>
    </div>
  );
}
